import { create } from 'zustand'
import { webViewParamsService } from '@/core/services/WebViewParamsService'
import { configService } from '@/core/services/ConfigService'

export type Theme = 'light' | 'dark' | 'system'
export type Language = 'en' | 'pt' | 'es' | 'fr'

export interface ThemeState {
  theme: Theme
  actualTheme: 'light' | 'dark'
  language: Language
  isWebViewMode: boolean
  webViewParams: {
    theme?: string
    language?: string
  }
  setTheme: (theme: Theme) => void
  setLanguage: (language: Language) => void
  toggleTheme: () => void
  initialize: () => void
}

const THEME_STORAGE_KEY = configService.ui.themeKey
const LANGUAGE_STORAGE_KEY = configService.ui.languageKey

const getSystemTheme = (): 'light' | 'dark' => {
  if (typeof window === 'undefined') return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light'
}

const normalizeLanguage = (lang: string): Language => {
  const normalized = lang.toLowerCase()
  if (normalized.startsWith('pt')) return 'pt'
  if (normalized.startsWith('es')) return 'es'
  if (normalized.startsWith('fr')) return 'fr'
  return 'en'
}

const normalizeTheme = (theme: string): Theme => {
  if (theme === 'dark' || theme === 'light') return theme as Theme
  return 'system'
}

const getInitialTheme = (webViewTheme?: string): Theme => {
  if (webViewTheme) {
    return normalizeTheme(webViewTheme)
  }

  if (typeof localStorage === 'undefined' || localStorage === null) {
    return 'system'
  }

  const storedTheme = localStorage.getItem(THEME_STORAGE_KEY) as Theme | null
  if (storedTheme && ['light', 'dark', 'system'].includes(storedTheme)) {
    return storedTheme
  }

  return 'system'
}

const getInitialLanguage = (webViewLanguage?: string): Language => {
  if (webViewLanguage) {
    return normalizeLanguage(webViewLanguage)
  }

  if (typeof localStorage === 'undefined' || localStorage === null) {
    const browserLang = navigator.language
    return normalizeLanguage(browserLang)
  }

  const storedLanguage = localStorage.getItem(LANGUAGE_STORAGE_KEY) as Language | null
  if (storedLanguage && ['en', 'pt', 'es', 'fr'].includes(storedLanguage)) {
    return storedLanguage
  }

  const browserLang = navigator.language
  return normalizeLanguage(browserLang)
}

const updateActualTheme = (currentTheme: Theme): 'light' | 'dark' => {
  return currentTheme === 'system' ? getSystemTheme() : currentTheme
}

export const useThemeStore = create<ThemeState>((set, get) => {
  const webViewTheme = webViewParamsService.getTheme()
  const webViewLanguage = webViewParamsService.getLanguage()
  const isWebViewMode = !!(webViewTheme || webViewLanguage)

  return {
    theme: 'system',
    actualTheme: 'light',
    language: 'en',
    isWebViewMode,
    webViewParams: {
      theme: webViewTheme,
      language: webViewLanguage,
    },

    setTheme: (newTheme: Theme) => {
      if (get().isWebViewMode) return
      if (typeof localStorage === 'undefined' || localStorage === null) return

      localStorage.setItem(THEME_STORAGE_KEY, newTheme)
      set({ theme: newTheme })
    },

    setLanguage: (newLanguage: Language) => {
      if (get().isWebViewMode) return
      if (typeof localStorage === 'undefined' || localStorage === null) return

      localStorage.setItem(LANGUAGE_STORAGE_KEY, newLanguage)
      set({ language: newLanguage })
    },

    toggleTheme: () => {
      const currentActualTheme = get().actualTheme
      const nextTheme: Theme = currentActualTheme === 'dark' ? 'light' : 'dark'
      get().setTheme(nextTheme)
    },

    initialize: () => {
      const initialTheme = getInitialTheme(webViewTheme)
      const initialLanguage = getInitialLanguage(webViewLanguage)
      const actualTheme = updateActualTheme(initialTheme)

      set({
        theme: initialTheme,
        language: initialLanguage,
        actualTheme,
      })

      // Apply theme to document
      const root = document.documentElement
      root.classList.remove('light', 'dark')
      root.classList.add(actualTheme)

      if (actualTheme === 'dark') {
        root.classList.add('dark')
      } else {
        root.classList.remove('dark')
      }

      // Set language
      root.lang = initialLanguage

      console.log('🎨 Theme initialized:', actualTheme, {
        original: initialTheme,
        isSystem: initialTheme === 'system',
        language: initialLanguage,
      })

      // Listen for system theme changes if using system theme
      if (initialTheme === 'system') {
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
        const handleChange = () => {
          const newActualTheme = updateActualTheme(initialTheme)
          set({ actualTheme: newActualTheme })

          const root = document.documentElement
          root.classList.remove('light', 'dark')
          root.classList.add(newActualTheme)

          if (newActualTheme === 'dark') {
            root.classList.add('dark')
          } else {
            root.classList.remove('dark')
          }
        }

        mediaQuery.addEventListener('change', handleChange)
        return () => mediaQuery.removeEventListener('change', handleChange)
      }
    },
  }
})